<script context="module" lang="ts">
  import { LinearFilter, RepeatWrapping, SRGBColorSpace, TextureLoader, Vector2 } from 'three';
  import { useLoader } from '@threlte/core';

  type TextureSourcesGroup = { [key: string]: string };
  type TexturesSources = { [key: string]: TextureSourcesGroup };

  type Textures = {
    [key: string]: {
      [key: string]: any;
    };
  };

  const fetchTextures = async (group: TextureSourcesGroup) =>
    (await useLoader(TextureLoader).load(group, undefined)) as any;

  const formatTextures = (textures: Textures) => {
    //   texture settings
    if (textures.shared) {
      textures.shared.clearCoat.repeat = new Vector2(10, 10);
      textures.shared.clearCoat.wrapS = RepeatWrapping;
      textures.shared.clearCoat.wrapT = RepeatWrapping;
      textures.shared.whitePlastic.colorSpace = SRGBColorSpace;
    }
    if (textures.register) {
      textures.register.x2ScreenBack.flipY = false;
      textures.register.x2ScreenBack.colorSpace = SRGBColorSpace;
      textures.register.x2ScreenBack.minFilter = LinearFilter;
      textures.register.x2ScreenFront.flipY = false;
      textures.register.x2ScreenFront.colorSpace = SRGBColorSpace;
      textures.register.x2ScreenFront.minFilter = LinearFilter;
    }
    if (textures.terminal) {
      textures.terminal.t2Screen.colorSpace = SRGBColorSpace;
      textures.terminal.t2Screen.minFilter = LinearFilter;
      textures.terminal.t2Normals.channel = 1;
      textures.terminal.t2Screen.flipY = false;
      textures.terminal.t2Ao.channel = 1;
      textures.terminal.t2Ao.flipY = false;
    }
    if (textures.stand) {
      textures.stand.s3Screen.colorSpace = SRGBColorSpace;
      textures.stand.s3Screen.minFilter = LinearFilter;
      textures.stand.s3Screen.flipY = false;
      textures.stand.s3Ao.flipY = false;
    }
    if (textures.laptop) {
      textures.laptop.laptopScreen.flipY = false;
      textures.laptop.laptopFrameNormals.flipY = false;
      textures.laptop.laptopFrameNormals.channel = 1;
      textures.laptop.laptopKeysMap.minFilter = LinearFilter;
      textures.laptop.laptopScreen.colorSpace = SRGBColorSpace;
      textures.laptop.laptopScreen.flipY = false;
      textures.laptop.laptopAo.flipY = false;
      textures.laptop.laptopAo.channel = 1;
      textures.laptop.laptopScreen.minFilter = LinearFilter;
    }
    if (textures.phone) {
      textures.phone.phoneScreen.minFilter = LinearFilter;
      textures.phone.phoneScreen.colorSpace = SRGBColorSpace;
      textures.phone.phoneScreen.flipY = false;
    }
    if (textures.card) {
      textures.card.cardFront.flipY = false;
      textures.card.cardFront.colorSpace = SRGBColorSpace;
      textures.card.cardBack.colorSpace = SRGBColorSpace;
      textures.card.cardBack.flipY = false;
      textures.card.cardAlpha.colorSpace = SRGBColorSpace;
      textures.card.cardAlpha.flipY = false;
      textures.card.cardNormal.flipY = false;
    }
    if (textures.handheld) {
      textures.handheld.t3Screen.colorSpace = SRGBColorSpace;
      textures.handheld.t3Screen.minFilter = LinearFilter;
      textures.handheld.t3Screen.flipY = false;
      textures.handheld.t3AO.channel = 0;
      textures.handheld.t3AO.flipY = false;
      textures.handheld.t3cameras.colorSpace = SRGBColorSpace;
      textures.handheld.t3cameras.flipY = false;
    }
    return textures;
  };

  // All texture files imported manually
  export const getSharedTextures = async (matList: TexturesSources, materialGroups: string[]) => {
    let textures: Textures = {};
    try {
      await Promise.all(
        materialGroups.map(async (groupKey) => {
          const result = await fetchTextures(matList[groupKey]);
          textures = { ...textures, [groupKey]: { ...result } };
        })
      );

      return formatTextures(textures);
    } catch (err) {
      return console.error('Failed to load texture', err);
    }
  };
</script>
